import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ApiUrl } from "./Constants";
import { getHeadereGerdauToken, geteGerdauAuthHeader } from "common/services";
import { withLocalNetworkAccess } from "./LocalNetworkAccess";
// Type-only import: erased at build time, so it introduces no runtime cycle with EgerdauApi.
import type { EgerdauCustomer } from "./EgerdauApi";

interface MuleResponse<T> {
  message: {
    messageType: string;
    data: T;
  };
}
type NestingServiceResult = MuleResponse<{ id: string }>;

interface NestingServiceArgs {
  token: string;
  body: FormData;
}

interface CustomerLocaleIds {
  soldTo: string;
  shipTo: string;
}

/**
 * Args for {@link QuickQuoteApi.getSAPAuxilaryData}. `shipTo` accepts multiple ship-to
 * identifiers — the backend matches against any of them (returning the first match) via the
 * `X-Ship-To` header, since a query string has practical length limits a customer's full
 * ship-to list could exceed.
 */
interface GetSAPAuxiliaryDataArgs {
  soldTo: string;
  shipTo: string[];
}

export interface SoldToDetail {
  salesOrg: string;
  distChannel: string;
  shippingCondition: string;
  incoTerms: string;
  currencyType: string;
  paymentTerms: string;
}

export interface SAPAuxProfile extends CustomerLocaleIds, SoldToDetail {}

interface UploadResult {
  uid: string;
  quoteId: number;
}

interface ApiResult<T> {
  data: T;
}

interface UploadResponse extends ApiResult<UploadResult> {
  error?: string;
  message?: string;
  errorType?: string;
}

export interface ListPrice {
  uom: string;
  currency: string;
  listPrice: number;
  listPriceUOM: string;
  lengthExtra: number;
  lengthExtraUOM: string;
  gradeExtra: number;
  gradeExtraUOM: string;
  sku: string;
  shipToCountry: string;
  error: string;
}
export interface FreightPrice {
  SHAPE: string;
  SUBFAMILY: string;
  FREIGHT: string;
  TRMODE: string;
  PRICEGROUP: string;
  FROMDATE: string;
  SIZE: string;
  TODATE: string;
  PLANT: string;
  CURRENCY: string;
  STATE: string;
  TRZONE: string;
  SHIPTOCITY: string;
}

export interface TotalPrice {
  item_uid: string;
  sku: string;
  pricingCost: string;
  pricingDescription: string;
}

export interface SoldToSearchResult {
  soldToId: string;
  soldToName: string;
}

type SoldToSearchResponse = ApiResult<SoldToSearchResult[]>;

const FormHeader = {
  "Content-Type": "multipart/form-data",
};

const transformUploadResponse = (response: UploadResponse): UploadResult => {
  if (response.data && !response.error && !response.message && !response.errorType) {
    return response.data;
  }

  if (response.error || response.message || response.errorType) {
    throw new Error(response.error || response.message || "Upload failed");
  }

  return response.data;
};

// Custom base query with error handling
const customBaseQuery = async (args: any, api: any, extraOptions: any) => {
  const result = await fetchBaseQuery({
    baseUrl: `${ApiUrl}/`,
    // Send the HttpOnly QQT_SESSION cookie so cookie-session (BFF) requests authenticate.
    credentials: "include",
    prepareHeaders: (headers) => {
      return headers;
    },
    fetchFn: async (input, init) => await fetch(input, withLocalNetworkAccess(input, init)),
  })(args, api, extraOptions);

  if (result.error) {
    const { status, data } = result.error as any;
    if (status === 400 && data && (data.message || data.error)) {
      const backendError = new Error(data.message || data.error || "Upload failed");
      (backendError as any).status = status;

      (backendError as any).data = data;
      (backendError as any).originalError = result.error;

      result.error = {
        status,
        data: {
          message: data.message || "Upload failed",
          error: data.error || "Unknown error",
          originalMessage: data.message,
          fieldError: data.fieldError,
          uuidTraceId: data.uuidTraceId,
        },
      } as any;
    }
  }
  return result;
};

export const QuickQuoteApi = createApi({
  reducerPath: "quickQuoteApi",
  baseQuery: customBaseQuery,
  endpoints: (build) => ({
    saveNestedFile: build.mutation<
      UploadResult,
      { body: FormData; token: string; isIntelligentUpload?: boolean }
    >({
      query: ({ body, token }) => {
        return {
          url: "file/upload",
          method: "POST",
          headers: geteGerdauAuthHeader(token),
          body,
        };
      },
      transformResponse: transformUploadResponse,
    }),
    saveUncombinedFile: build.mutation<
      UploadResult,
      { body: FormData; token: string; isIntelligentUpload?: boolean }
    >({
      query: ({ body, token }) => {
        return {
          url: "file/upload/unnested",
          method: "POST",
          headers: geteGerdauAuthHeader(token),
          body,
        };
      },
      transformResponse: transformUploadResponse,
    }),
    saveQuotePreferences: build.mutation<any, { body: FormData; token: string }>({
      query: ({ body, token }) => ({
        url: "file/request_date",
        method: "POST",
        headers: {
          ...FormHeader,
          ...geteGerdauAuthHeader(token),
        },
        body,
      }),
    }),
    sendFileToNestingService: build.mutation<string, { args: NestingServiceArgs; token: string }>({
      query: ({
        args,
        token,
      }): { url: string; method: string; headers: Record<string, string>; body: any } => ({
        url: "mule/nesting",
        method: "POST",
        headers: {
          Authorization: getHeadereGerdauToken(token),
        },
        body: args.body,
      }),
      transformResponse: (response: NestingServiceResult) => response.message.data.id,
    }),
    getSAPAuxilaryData: build.query<SoldToDetail, { token: string; args: GetSAPAuxiliaryDataArgs }>(
      {
        query: ({ token, args }) => ({
          url: "customer/sold_to",
          headers: {
            Authorization: `Bearer ${token}`,
            // Comma-separated ship-to list. Preferred over stacking `shipTo` query params: query
            // strings have practical/browser length limits a customer's full ship-to list could
            // exceed, and this keeps the URL itself short and log-friendly.
            "X-Ship-To": args.shipTo.join(","),
          },
          params: { soldTo: args.soldTo },
        }),
        transformResponse: (response: ApiResult<SoldToDetail>) => response.data,
      },
    ),
    /**
     * The signed-in user's profile, resolved by the backend from the session email via the
     * MuleSoft API. Same payload as the legacy eGerdau
     * `GET /rest/v2/egerdauNA/users/current`, so it deserialises into {@link EgerdauCustomer}
     * unchanged. Authenticated by the QQT_SESSION cookie (`credentials: "include"` above) — no
     * bearer token and no email parameter: the backend never accepts the email from the caller.
     * Selected by the `toggleQqtUserProfileEndpoint` toggle.
     */
    getQqtUserProfile: build.query<EgerdauCustomer, void>({
      query: () => ({
        url: "users/current",
      }),
    }),
    searchSoldTo: build.query<SoldToSearchResult[], { token: string; name: string }>({
      query: ({ token, name }) => ({
        url: "customer/sold_to/search",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { name },
      }),
      transformResponse: (response: SoldToSearchResponse) => response.data,
    }),
  }),
});

export const {
  useGetSAPAuxilaryDataQuery,
  useGetQqtUserProfileQuery,
  useSendFileToNestingServiceMutation,
  useSaveUncombinedFileMutation,
  useSaveNestedFileMutation,
  useLazySearchSoldToQuery,
} = QuickQuoteApi;
